/** Chip colours, after cnt-landing's STATUS_META (libs/shared-cnt/models/src/lib/booking.types.ts). */
export type ChipTone = 'green' | 'gold' | 'trinidad' | 'grey' | 'teal';

export type StatusKind = 'reservation' | 'payout' | 'listing' | 'poi' | 'log' | 'user' | 'article' | 'role';

export interface StatusChip {
  label: string;
  tone: ChipTone;
}

const MAPS: Record<StatusKind, Record<string, StatusChip>> = {
  // objects/src/enums/status.ts
  reservation: {
    requested: { label: 'Requested', tone: 'gold' },
    change: { label: 'Change requested', tone: 'gold' },
    processing: { label: 'Processing', tone: 'gold' },
    accepted: { label: 'Accepted', tone: 'green' },
    paid: { label: 'Paid', tone: 'green' },
    rated: { label: 'Rated', tone: 'green' },
    completed: { label: 'Completed', tone: 'green' },
    rejected: { label: 'Declined', tone: 'trinidad' },
    cancelled: { label: 'Cancelled', tone: 'grey' },
  },
  // complete-payout-modal's newStatus; the server's financial.service checks the same three.
  payout: {
    pending: { label: 'Pending', tone: 'gold' },
    paid: { label: 'Paid', tone: 'green' },
    declined: { label: 'Declined', tone: 'trinidad' },
  },
  // Listings and boondock sites share these.
  listing: {
    available: { label: 'Live', tone: 'green' },
    unavailable: { label: 'Off', tone: 'grey' },
    featured: { label: '★ Featured', tone: 'gold' },
    deleted: { label: 'Deleted', tone: 'trinidad' },
  },
  poi: {
    approved: { label: 'Approved', tone: 'green' },
    awaiting: { label: 'Awaiting approval', tone: 'gold' },
    hidden: { label: 'Hidden', tone: 'grey' },
    deleted: { label: 'Deleted', tone: 'trinidad' },
  },
  // objects/src/enums/log-levels.ts
  log: {
    critical: { label: 'Critical', tone: 'trinidad' },
    error: { label: 'Error', tone: 'trinidad' },
    warning: { label: 'Warning', tone: 'gold' },
    update: { label: 'Update', tone: 'teal' },
    info: { label: 'Info', tone: 'grey' },
    debug: { label: 'Debug', tone: 'grey' },
    trace: { label: 'Trace', tone: 'grey' },
  },
  user: {
    deleted: { label: 'Deleted', tone: 'trinidad' },
  },
  // objects/src/enums/enums.ts Role; admins stand out, everyone else is grey.
  role: {
    admin: { label: 'Admin', tone: 'teal' },
    salesmanager: { label: 'Sales manager', tone: 'grey' },
    report: { label: 'Reports only', tone: 'grey' },
  },
  article: {
    published: { label: 'Published', tone: 'green' },
    draft: { label: 'Draft', tone: 'gold' },
  },
};

/** The label and colour for a status value; unknown values are grey, title-cased. */
export function statusChip(kind: StatusKind, value: string | null | undefined): StatusChip {
  const key = String(value ?? '')
    .trim()
    .toLowerCase();
  return MAPS[kind][key] ?? { label: titleCase(key) || '—', tone: 'grey' };
}

function titleCase(s: string): string {
  return s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
